import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

// Define the feedback data interface
interface FeedbackData {
  name: string;
  rating: number;
  conversation_id: string;
  comment: string;
}

// Define the state interface
interface FeedbackState {
  loading: boolean;
  success: boolean;
  error: string | null;
}

// Initial state
const initialState: FeedbackState = {
  loading: false,
  success: false,
  error: null
};

// Helper function to get city information from URL
const getCityInfo = (): { city_code: string; city_name: string } => {
  const urlParams = new URLSearchParams(window.location.search);
  const keys = Array.from(urlParams.keys());
  
  for (const key of keys) {
    const value = urlParams.get(key);
    
    // Format 1: ?aubrey-tx (key is the city, value is empty)
    if (key && !value && key.includes('-')) {
      const cityName = key.split('-')[0];
      const stateName = key.split('-')[1]?.toUpperCase() || '';
      return {
        city_code: `city-of-${cityName}`,
        city_name: `City of ${cityName.charAt(0).toUpperCase() + cityName.slice(1)}${stateName ? ', ' + stateName : ''}`
      };
    }
    // Format 2: ?community=aubrey-tx
    if (key === 'community' && value) {
      const cityName = value.split('-')[0];
      const stateName = value.split('-')[1]?.toUpperCase() || '';
      return {
        city_code: `city-of-${cityName}`,
        city_name: `City of ${cityName.charAt(0).toUpperCase() + cityName.slice(1)}${stateName ? ', ' + stateName : ''}`
      };
    }
  }
  
  // Default city if none detected
  return {
    city_code: 'city-of-default',
    city_name: 'Default City'
  };
};

// Create async thunk for submitting feedback
export const submitFeedback = createAsyncThunk(
  'feedback/submit',
  async (feedbackData: FeedbackData, { rejectWithValue }) => {
    try {
      const cityInfo = getCityInfo();
      
      // Prepare payload with new structure
      const payload = {
        rating: feedbackData.rating,
        phone_number: 'N/A',
        city_code: cityInfo.city_code,
        city_name: cityInfo.city_name
      };
      
      const response = await axios.post('https://aichat-api.viewprogis.com/api/feedback/submit', payload);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data || error.message);
      }
      return rejectWithValue('An unknown error occurred');
    }
  }
);

// Create the feedback slice
const feedbackSlice = createSlice({
  name: 'feedback',
  initialState,
  reducers: {
    clearFeedbackState: (state) => {
      state.loading = false;
      state.success = false;
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(submitFeedback.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.error = null;
      })
      .addCase(submitFeedback.fulfilled, (state) => {
        state.loading = false;
        state.success = true;
        state.error = null;
      })
      .addCase(submitFeedback.rejected, (state, action) => {
        state.loading = false;
        state.success = false;
        state.error = action.payload as string;
      });
  }
});

export const { clearFeedbackState } = feedbackSlice.actions;
export default feedbackSlice.reducer;
